import { Box, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Container, Heading, Text } from '../atomic';
import { StyledButton } from '../ui';

/**
 * @typedef {Object} FinalCTASectionProps
 * @property {string} [sectionId] - Section ID for navigation
 * @property {string} [headline] - Section headline
 * @property {string} [subheadline] - Section subheadline
 * @property {string} [primaryCtaText] - Primary CTA button text
 * @property {string} [primaryCtaHref] - Primary CTA button link
 * @property {string} [secondaryCtaText] - Secondary CTA button text
 * @property {string} [secondaryCtaHref] - Secondary CTA button link
 * @property {string} [supportingText] - Supporting text below CTAs
 * @property {Function} [onPrimaryCtaClick] - Primary CTA click handler
 * @property {Function} [onSecondaryCtaClick] - Secondary CTA click handler
 */

/**
 * Final CTA Block Section
 * Inspired by Stripe + Crextio
 * Uses design tokens for consistent theming.
 *
 * @param {FinalCTASectionProps} props - Component props
 * @returns {JSX.Element} Final CTA section
 */
function FinalCTASection({
  sectionId = 'final-cta',
  headline = 'Ready to Reach FinOps Zen?',
  subheadline,
  primaryCtaText = 'Get Early Access',
  primaryCtaHref = '/early-access',
  secondaryCtaText = 'See Benefits',
  secondaryCtaHref = '/early-access#benefits',
  supportingText = null,
  onPrimaryCtaClick,
  onSecondaryCtaClick,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Box
      component="section"
      id={sectionId}
      ref={sectionRef}
      aria-label="Call to action"
      sx={{
        py: { xs: 10, md: 14 },
        // Use footer background (dark purple) for strong contrast
        backgroundColor: theme.custom.footerBackground,
        position: 'relative',
        overflow: 'hidden',
        // Top green border
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          backgroundColor: theme.palette.secondary.main,
        },
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease',
          }}
        >
          {/* Headline */}
          <Heading
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.75rem' },
              fontWeight: 700,
              color: theme.palette.primary.contrastText,
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            {headline}
          </Heading>

          {/* Subheadline */}
          <Text
            sx={{
              fontSize: { xs: '1.1rem', md: '1.35rem' },
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: '550px',
              mx: 'auto',
              mb: 5,
              lineHeight: 1.7,
              textAlign: 'center',
            }}
          >
            {subheadline || (
              <>
                Join the early access program and put the{' '}
                <span
                  style={{
                    color: theme.palette.secondary.main,
                    fontWeight: 900,
                  }}
                >
                  Fin
                </span>{' '}
                in{' '}
                <span
                  style={{
                    color: theme.palette.secondary.main,
                    fontWeight: 900,
                  }}
                >
                  Fin
                </span>
                Ops.
              </>
            )}
          </Text>

          {/* CTA Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'center',
              mb: 4,
            }}
          >
            <StyledButton
              variant="outlined"
              size="large"
              href={primaryCtaHref}
              onClick={onPrimaryCtaClick}
              sx={{
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                color: '#ffffff',
              }}
            >
              {primaryCtaText}
            </StyledButton>
            {secondaryCtaText && (
              <StyledButton
                variant="contained"
                size="large"
                href={secondaryCtaHref}
                onClick={onSecondaryCtaClick}
                sx={{
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  backgroundColor: '#f4f0eb',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: '#ebe5dd',
                  },
                }}
              >
                {secondaryCtaText}
              </StyledButton>
            )}
          </Box>

          {/* Supporting Text */}
          {supportingText && (
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.95rem',
              }}
            >
              {supportingText}
            </Typography>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default FinalCTASection;
